// Primero de todo: el retorno de un OAuth iniciado desde la app se va a Cyclone sin cargar nada.
import { mobileOAuthForward } from '@/auth/mobileOAuthForward';
import '@/auth/instagramCallback';
import '@/auth/googleBusinessCallback';
import React from 'react';
import { createRoot } from 'react-dom/client';
// Register all dayjs plugins/locale at boot, before any component renders, so
// chunk load order can never leave a plugin method (e.g. isSameOrAfter)
// undefined. Must stay first so the extends run before App's module graph.
import '@/dayjs-setup';
import { initAutologin } from '@/autologin-bootstrap';
// Foto del storage ANTES de que monte React y, sobre todo, antes de que
// <Auth0Provider> corra su checkSession(): a partir de ahí el SDK puede haber
// limpiado su propio cache y ya no se puede saber si el localStorage sobrevivió.
// Es el dato que decide si el fix de la sesión en Safari es prender
// allow_offline_access o algo más. Ver auth/sessionDiagnostics.ts.
import { captureBootSnapshot } from '@/auth/sessionDiagnostics';
// El hash con los tokens de Auth0 se saca de la URL ACA, antes de montar React:
// hacerlo dentro del render de <Auth0Callback> dejó de ser confiable cuando la
// ruta pasó a ser lazy. Ver auth/callbackHash.ts.
import { captureCallbackHashAtBoot } from '@/auth/callbackHash';
import { installDomTranslateGuard } from '@/dom-translate-guard';
import { installDomAutofillGuard } from '@/dom-autofill-guard';
import { App } from '@/components';
// Montserrat autoalojada + su fallback métrico, en el CSS principal: las caras
// existen desde el primer render (ver el comentario del archivo).
import './styles/fonts.css';
import './styles/app.css';
// Keep client fichas styled before their lazy routes mount. Vercel's pinned
// dynamic imports currently lose Vite's route CSS preload dependencies.
import './containers/ClientFields/client-fields.css';
// The guides are shared by six lazy routes and announcements. Load their small
// stylesheet eagerly too, so pinned production imports cannot orphan its CSS.
import './components/ActivationGuide/activation-guide.css';
// La ficha de Google (tarjeta de invitación, Ajustes y respuesta) vive en rutas lazy: misma razón.
import './containers/Reviews/google-business.css';
// Fichaje del equipo (pantalla del local y resultado del escaneo): rutas lazy, misma razón.
import './containers/Attendance/attendance.css';
// Premios (el pase de cada premio): ruta lazy, misma razón.
import './containers/Marketing/Premios/premios.css';
// CSS de react-phone-input-2 EAGER: InputPhone es lazy (solo en rutas de forms) y su
// CSS side-effect cargaba con el chunk lazy → llegaba DESPUÉS del render (FOUC: el campo
// de teléfono + el dropdown de código de área aparecían SIN estilo / desalineados,
// "flasheaban" al tipear). Cargándolo acá (eager, después de Tailwind para ganar
// especificidad) el estilo siempre está presente antes del primer render.
import 'react-phone-input-2/lib/style.css';
import { registerServiceWorker } from '@/pwa';
import { installChunkSafeMode } from '@/components/App/chunkPreloadError';
import { withReactProfiler } from '@/perf/reactProfiler';

declare global {
  interface Window {
    __flashBootstrapStarted?: boolean;
    __agoraMainLoaded?: () => void;
  }
}

const root = document.getElementById('root') as HTMLElement;

// Una misma build puede referenciar el entry con y sin query string si se rompe el
// pinning de Skew Protection. El navegador considera esas URLs módulos distintos;
// este guard evita que una segunda evaluación monte otro árbol React en #root.
// Con un retorno de OAuth de la app en camino a Cyclone, Flash NO arranca: si montara,
// su pantalla de callback (con la sesión web abierta en el mismo navegador) podía gastar
// el `code` de un solo uso antes de que llegue a Cyclone.
if (!window.__flashBootstrapStarted && !mobileOAuthForward) {
  window.__flashBootstrapStarted = true;

  // Lo PRIMERO: cualquier cosa que corra después puede tocar storage o Auth0.
  captureBootSnapshot();

  // Inmediatamente después: el hash de `/callback` se guarda y se borra de la URL
  // acá, en el boot. Mientras <Auth0Callback> era eager alcanzaba con capturarlo en
  // su render; desde que la ruta es lazy ese render llega tarde y puede repetirse,
  // y el hash se perdía en el medio (73% de los callbacks). No toca ninguna otra
  // ruta: adentro chequea que el pathname sea /callback.
  captureCallbackHashAtBoot();

  // Vite strips `onload` from the source module tag in production, so invoke
  // the early inline probe from code that survives the production transform.
  window.__agoraMainLoaded?.();

  // Antes de cualquier import() de ruta: si esta pestaña quedó en modo seguro tras dos
  // chunks fallidos seguidos, las rutas lazy bajan sin la ráfaga de modulepreload.
  // No-op en el resto de las pestañas. Ver chunkPreloadError.ts.
  installChunkSafeMode();

  // El tráfico pago aterriza en /comenzar: la bajada de su chunk arranca acá,
  // en paralelo al bootstrap, en vez de esperar el mount del Router. Mismo
  // specifier que la ruta lazy para que Vite lo resuelva al mismo archivo.
  if (window.location.pathname === '/comenzar') {
    // NO reintentar acá: un import() que rechaza deja la entrada del module map
    // envenenada para esa URL en TODO el documento, así que un segundo import del
    // mismo specifier rechaza al instante sin pedir nada a la red (verificado en
    // Chromium, incluso con la red ya restaurada). Encima el helper de preload de
    // Vite no propaga el rechazo, porque App.tsx hace preventDefault() sobre
    // 'vite:preloadError'. La única recuperación real cuando el chunk no llega es
    // arrancar un documento nuevo: la hace el watchdog de adopción del hero
    // estático en index.html, que recarga una sola vez por visita.
    import('@/containers/CampaignLanding').catch(() => {});
  }
  // Misma precarga para la landing de Ágora UP (mismo patrón, mismo caveat).
  if (window.location.pathname === '/comenzar-up') {
    import('@/containers/AgoraUpLanding').catch(() => {});
  }

  // Antes de montar React: blinda removeChild/insertBefore contra los traductores
  // del navegador (Safari Translate iOS / Google Translate), que reemplazan text
  // nodes y crashean la reconciliación de React ("The object can not be found
  // here"). Ver src/dom-translate-guard.ts.
  installDomTranslateGuard();

  // El autocompletado del navegador es OPT-IN en Flash: cualquier input que no
  // declare `autocomplete` queda apagado. Evita que el perfil guardado (mail,
  // nombre, dirección) se escriba solo en campos que esperan OTRO dato — así se
  // colaban mails mutilados como nombre de usuario. Ver src/dom-autofill-guard.ts.
  installDomAutofillGuard();

  // Si la URL trae un token de autologin/handoff (escape IG-webview → navegador real),
  // canjearlo/sembrarlo ANTES de montar React, así la app bootea ya autenticada en vez
  // de rebotar a /login. Para cargas normales resuelve de inmediato (no hay token).
  initAutologin().finally(() => {
    createRoot(root).render(withReactProfiler('app', <App />));

    // Cachea los chunks localmente para que los deploys no le tiren la pantalla de
    // "Hay una versión nueva" a nadie. Después del render para no competir con el
    // primer paint.
    registerServiceWorker();
  });
}
